// ===== BEGIN FILE: src/components/QuickAccessMenu.tsx =====
import React, { useState, useRef } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemDecorator,
  Dropdown,
  MenuButton,
  Typography,
  Divider,
  Box
} from '@mui/joy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TimelineIcon from '@mui/icons-material/Timeline';
import BoltIcon from '@mui/icons-material/Bolt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import PublicIcon from '@mui/icons-material/Public';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GridViewIcon from '@mui/icons-material/GridView';

interface QuickAccessMenuProps {
  onSwotAnalysis: () => void;
  onGetRemedies: () => void;
  onCurrentPeriodAnalysis: () => void;
  onLifeAreasDashboard?: () => void;
  onYogasAnalysis?: () => void;
  onDashaTimeline?: () => void;
  onFutureEventPredictor?: () => void;
  onPlanetImpact?: () => void;
  onActivitiesBalance?: () => void;
  onMarriageCompatibility?: () => void;
  onFamilyScoreCard?: () => void;
  onLifeEventFinder?: () => void;
  onPersonalMuhurta?: () => void;  onHouseDashboard?: () => void;  onFloatingChart?: () => void;  // Admin-only: Open floating chart modal
  isAdmin?: boolean;             // Whether user is admin
  disabled?: boolean;
  isMobile?: boolean;            // Whether to render as icon button for mobile
  aiCreditsGeneral?: number | null;
  aiCreditsLifeArea?: number | null;
}

export default function QuickAccessMenu({
  onSwotAnalysis,
  onGetRemedies,
  onCurrentPeriodAnalysis,
  onLifeAreasDashboard,
  onYogasAnalysis,
  onDashaTimeline,
  onFutureEventPredictor,
  onPlanetImpact,
  onActivitiesBalance,
  onMarriageCompatibility,
  onFamilyScoreCard,
  onLifeEventFinder,
  onPersonalMuhurta,
  onHouseDashboard,
  onFloatingChart,
  isAdmin = false,
  disabled = false,
  isMobile = false,
  aiCreditsGeneral = null,
  aiCreditsLifeArea = null
}: QuickAccessMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSwotClick = () => {
    setMenuOpen(false);
    onSwotAnalysis();
  };

  const handleRemediesClick = () => {
    setMenuOpen(false);
    onGetRemedies();
  };

  const handlePeriodClick = () => {
    setMenuOpen(false);
    onCurrentPeriodAnalysis();
  };

  const handleLifeAreasClick = () => {
    setMenuOpen(false);
    onLifeAreasDashboard?.();
  };

  const handleYogasClick = () => {
    setMenuOpen(false);
    onYogasAnalysis?.();
  };

  const handleDashaTimelineClick = () => {
    setMenuOpen(false);
    onDashaTimeline?.();
  };

  const handleFutureEventsClick = () => {
    setMenuOpen(false);
    onFutureEventPredictor?.();
  };

  const handlePlanetImpactClick = () => {
    setMenuOpen(false);
    onPlanetImpact?.();
  };

  const handleActivitiesClick = () => {
    setMenuOpen(false);
    onActivitiesBalance?.();
  };

  const handleMarriageCompatibilityClick = () => {
    setMenuOpen(false);
    onMarriageCompatibility?.();
  };

  const handleFamilyScoreCardClick = () => {
    setMenuOpen(false);
    onFamilyScoreCard?.();
  };

  const handleLifeEventFinderClick = () => {
    setMenuOpen(false);
    onLifeEventFinder?.();
  };

  const handlePersonalMuhurtaClick = () => {
    setMenuOpen(false);
    onPersonalMuhurta?.();
  };

  const handleHouseDashboardClick = () => {
    setMenuOpen(false);
    onHouseDashboard?.();
  };

  const handleFloatingChartClick = () => {
    setMenuOpen(false);
    onFloatingChart?.();
  };

  return (
    <Dropdown open={menuOpen} onOpenChange={(_, isOpen) => setMenuOpen(isOpen)}>
      {isMobile ? (
        // Mobile: Icon button only
        <MenuButton
          slots={{ root: 'button' }}
          slotProps={{
            root: {
              style: {
                border: 'none',
                background: 'rgba(102, 126, 234, 0.12)',
                color: '#667eea',
                borderRadius: '8px',
                padding: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                minHeight: '32px',
                flexShrink: 0
              }
            }
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
        </MenuButton>
      ) : (
        // Desktop: Full button with text
        <MenuButton
          slots={{ root: Button }}
          slotProps={{
            root: {
              size: 'sm',
              variant: 'solid',
              color: 'primary',
              disabled: disabled,
              sx: {
                fontSize: { xs: '0.8125rem', sm: '0.8125rem' },
                px: 1.25,
                py: 0.375,
                minWidth: 'auto',
                minHeight: 'auto',
                height: 'auto',
                fontFamily: '"Cinzel", "Garamond", serif',
                fontWeight: 600,
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                }
              }
            }
          }}
          endDecorator={<KeyboardArrowDownIcon />}
        >
          <BoltIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
          Quick Access
        </MenuButton>
      )}
      <Menu
        placement={isMobile ? "bottom-end" : "bottom-start"}
        sx={{
          minWidth: isMobile ? 240 : 520,
          maxWidth: isMobile ? 280 : 560,
          maxHeight: '80vh',
          overflowY: 'auto',
          '--List-padding': '0.25rem',
          '--ListItem-radius': '0.375rem',
          '--ListItem-minHeight': '36px',
          zIndex: 10002,
          mt: isMobile ? 0.5 : 5
        }}
      >
        {/* Two-column layout for desktop, single column for mobile */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 0.5,
          p: 0.5
        }}>
        <MenuItem onClick={handleSwotClick} sx={{ py: 0.5 }}>
          <ListItemDecorator sx={{ minWidth: 32 }}>
            <AssessmentIcon sx={{ fontSize: '1.1rem' }} />
          </ListItemDecorator>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              SWOT Analysis for My Chart
            </Typography>
            {aiCreditsGeneral !== null && (
              <Box component="span" sx={{ px: 0.6, py: '1px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: 700, lineHeight: '14px', flexShrink: 0, bgcolor: aiCreditsGeneral === 0 ? '#fee2e2' : aiCreditsGeneral <= 2 ? '#fef3c7' : '#dcfce7', color: aiCreditsGeneral === 0 ? '#dc2626' : aiCreditsGeneral <= 2 ? '#b45309' : '#16a34a' }}>
                {aiCreditsGeneral === 999999 ? '∞' : `${aiCreditsGeneral} left`}
              </Box>
            )}
          </Box>
        </MenuItem>

        <MenuItem onClick={handleRemediesClick} sx={{ py: 0.5 }}>
          <ListItemDecorator sx={{ minWidth: 32 }}>
            <LocalHospitalIcon sx={{ fontSize: '1.1rem' }} />
          </ListItemDecorator>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Get Life Area Remedies
            </Typography>
            {aiCreditsLifeArea !== null && (
              <Box component="span" sx={{ px: 0.6, py: '1px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: 700, lineHeight: '14px', flexShrink: 0, bgcolor: aiCreditsLifeArea === 0 ? '#fee2e2' : aiCreditsLifeArea <= 2 ? '#fef3c7' : '#dcfce7', color: aiCreditsLifeArea === 0 ? '#dc2626' : aiCreditsLifeArea <= 2 ? '#b45309' : '#16a34a' }}>
                {aiCreditsLifeArea === 999999 ? '∞' : `${aiCreditsLifeArea} left`}
              </Box>
            )}
          </Box>
        </MenuItem>

        <MenuItem onClick={handlePeriodClick} sx={{ py: 0.5 }}>
          <ListItemDecorator sx={{ minWidth: 32 }}>
            <TimelineIcon sx={{ fontSize: '1.1rem' }} />
          </ListItemDecorator>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Current Period Analysis
            </Typography>
            {aiCreditsGeneral !== null && (
              <Box component="span" sx={{ px: 0.6, py: '1px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: 700, lineHeight: '14px', flexShrink: 0, bgcolor: aiCreditsGeneral === 0 ? '#fee2e2' : aiCreditsGeneral <= 2 ? '#fef3c7' : '#dcfce7', color: aiCreditsGeneral === 0 ? '#dc2626' : aiCreditsGeneral <= 2 ? '#b45309' : '#16a34a' }}>
                {aiCreditsGeneral === 999999 ? '∞' : `${aiCreditsGeneral} left`}
              </Box>
            )}
          </Box>
        </MenuItem>

        {onLifeAreasDashboard && (
          <MenuItem onClick={handleLifeAreasClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <DashboardIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Life Areas Dashboard
            </Typography>
          </MenuItem>
        )}

        {onYogasAnalysis && (
          <MenuItem onClick={handleYogasClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <AutoAwesomeIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Active Yogas & Doshas
            </Typography>
          </MenuItem>
        )}

        {onDashaTimeline && (
          <MenuItem onClick={handleDashaTimelineClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <CalendarMonthIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Dasha Timeline View
            </Typography>
          </MenuItem>
        )}

        {onFutureEventPredictor && (
          <MenuItem onClick={handleFutureEventsClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <EventIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Future Events Predictor
            </Typography>
          </MenuItem>
        )}

        {onPlanetImpact && (
          <MenuItem onClick={handlePlanetImpactClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <PublicIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Planet Impact
            </Typography>
          </MenuItem>
        )}

        {onActivitiesBalance && (
          <MenuItem onClick={handleActivitiesClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <HomeWorkIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Activities to Balance Houses
            </Typography>
          </MenuItem>
        )}

        {onMarriageCompatibility && (
          <MenuItem onClick={handleMarriageCompatibilityClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <FavoriteIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Marriage Compatibility
            </Typography>
          </MenuItem>
        )}

        {onFamilyScoreCard && (
          <MenuItem onClick={handleFamilyScoreCardClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <GroupsIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Family Score Card
            </Typography>
          </MenuItem>
        )}

        {onLifeEventFinder && (
          <MenuItem onClick={handleLifeEventFinderClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <SearchIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Life Event Finder
            </Typography>
          </MenuItem>
        )}

        {onPersonalMuhurta && (
          <MenuItem onClick={handlePersonalMuhurtaClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <AccessTimeIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              Personal Muhurta Advisor
            </Typography>
          </MenuItem>
        )}

        {onHouseDashboard && (
          <MenuItem onClick={handleHouseDashboardClick} sx={{ py: 0.5 }}>
            <ListItemDecorator sx={{ minWidth: 32 }}>
              <GridViewIcon sx={{ fontSize: '1.1rem' }} />
            </ListItemDecorator>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
              House Compact View
            </Typography>
          </MenuItem>
        )}

        </Box>

        {/* Admin-only features */}
        {isAdmin && onFloatingChart && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={handleFloatingChartClick}
              sx={{
                py: 0.5,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                }
              }}
            >
              <ListItemDecorator sx={{ minWidth: 32 }}>
                <AccountTreeIcon sx={{ fontSize: '1.1rem', color: 'primary.600' }} />
              </ListItemDecorator>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                <Typography level="body-sm" sx={{ fontWeight: 600, color: 'primary.700' }}>
                  Floating Chart
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    ml: 'auto',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 'xs',
                    bgcolor: 'primary.100',
                    color: 'primary.700',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Admin
                </Typography>
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>
    </Dropdown>
  );
}
